import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlertLog } from "@/api/districts";
import type { AlertMeterType } from "@/api/districts/types";
import { DataTablePagination } from "@/components/table/data-table-pagination";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 10;

type MeterAlertsTableProps = {
	serialNumber: string;
	type: AlertMeterType;
};

export function MeterAlertsTable({
	serialNumber,
	type,
}: MeterAlertsTableProps) {
	const { t, i18n } = useTranslation();
	const [page, setPage] = useState(1);
	const { data, isLoading } = useAlertLog({
		type,
		serial_number: serialNumber,
		page,
		page_size: PAGE_SIZE,
	});

	const alerts = data?.results ?? [];
	const totalCount = data?.count ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

	return (
		<div className="flex flex-col gap-3">
			<div className="rounded-md border">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b bg-muted/50">
							<th className="px-4 py-3 text-left font-medium">
								{t("meterAlerts.colAlertType")}
							</th>
							<th className="px-4 py-3 text-left font-medium">
								{t("meterAlerts.colDate")}
							</th>
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							Array.from({ length: 3 }).map((_, i) => (
								<tr key={i} className="border-b last:border-0">
									<td className="px-4 py-3">
										<Skeleton className="h-4 w-32" />
									</td>
									<td className="px-4 py-3">
										<Skeleton className="h-4 w-28" />
									</td>
								</tr>
							))
						) : alerts.length > 0 ? (
							alerts.map((alert) => (
								<tr key={alert.id} className="border-b last:border-0">
									<td className="px-4 py-3 font-medium">{alert.alarm}</td>
									<td className="px-4 py-3 text-muted-foreground">
										{new Intl.DateTimeFormat(i18n.language, {
											dateStyle: "short",
											timeStyle: "short",
										}).format(new Date(alert.cap_dt))}
									</td>
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={2}
									className="px-4 py-8 text-center text-muted-foreground"
								>
									{t("meterAlerts.noAlerts")}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{totalPages > 1 && (
				<DataTablePagination
					page={page}
					pageSize={PAGE_SIZE}
					count={totalCount}
					onPageChange={setPage}
					onPageSizeChange={(_size) => undefined}
					hasPrevious={page > 1}
					hasNext={page < totalPages}
				/>
			)}
		</div>
	);
}
